/**
 * SearchDropdown Component
 * 
 * Renders the autocomplete dropdown for SearchBar with:
 * - Suggestion list with highlighting
 * - Error states
 * - Loading states
 * - Empty states
 * - Keyboard navigation support
 * - Accessibility features
 */

import React from 'react';
import { SearchBarState, AutocompleteSuggestion } from '../types/search';
import { highlightMatch } from './SearchBar.utils';

// Dropdown styles
const dropdownStyles = {
  dropdown: `
    absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg
    shadow-lg z-50 max-h-96 overflow-y-auto
  `,
  dropdownHidden: 'hidden',
  suggestionsList: 'py-2',
  suggestion: `
    px-4 py-3 cursor-pointer flex items-center justify-between
    hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
    transition-colors duration-150
  `,
  suggestionSelected: 'bg-[#E6EFEA] hover:bg-[#E6EFEA] focus:bg-[#E6EFEA]',
  suggestionText: 'flex-1 text-left',
  suggestionType: 'text-xs text-gray-500 ml-2 uppercase',
  noResults: 'px-4 py-6 text-center text-gray-500',
  error: 'px-4 py-3 text-red-600 bg-red-50 border-l-4 border-red-500',
};

interface SearchDropdownProps {
  state: SearchBarState;
  componentId: string;
  dropdownId: string;
  showDropdown: boolean;
  onSuggestionClick: (suggestion: AutocompleteSuggestion) => void;
  onSuggestionHover: (index: number) => void;
  listRef: React.RefObject<HTMLUListElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
}

/**
 * SearchDropdown component for displaying autocomplete suggestions
 */
export const SearchDropdown: React.FC<SearchDropdownProps> = React.memo(({
  state,
  componentId,
  dropdownId,
  showDropdown,
  onSuggestionClick,
  onSuggestionHover,
  listRef,
  dropdownRef,
}) => {
  return (
    <div
      ref={dropdownRef}
      id={dropdownId}
      className={`${dropdownStyles.dropdown} ${!showDropdown ? dropdownStyles.dropdownHidden : ''}`}
      role="listbox"
      aria-label="Search suggestions"
    >
      {/* Error state */}
      {state.error && (
        <div
          id={`${componentId}-error`}
          className={dropdownStyles.error}
          role="alert"
        >
          {state.error}
        </div>
      )}

      {/* Suggestions list */}
      {!state.error && state.suggestions.length > 0 && (
        <ul ref={listRef} className={dropdownStyles.suggestionsList} role="none">
          {state.suggestions.map((suggestion, index) => (
            <SuggestionItem
              key={suggestion.id}
              suggestion={suggestion}
              index={index}
              componentId={componentId}
              isSelected={index === state.selectedIndex}
              query={state.query}
              onClick={() => onSuggestionClick(suggestion)}
              onHover={() => onSuggestionHover(index)}
            />
          ))}
        </ul>
      )}

      {/* No results state */}
      {!state.error && !state.isLoading && state.query && state.suggestions.length === 0 && (
        <div className={dropdownStyles.noResults}>
          No results found for &quot;{state.query}&quot;
        </div>
      )}
    </div>
  );
});

SearchDropdown.displayName = 'SearchDropdown';

/**
 * Individual suggestion item component
 */
interface SuggestionItemProps {
  suggestion: AutocompleteSuggestion;
  index: number;
  componentId: string;
  isSelected: boolean;
  query: string;
  onClick: () => void;
  onHover: () => void;
}

const SuggestionItem: React.FC<SuggestionItemProps> = React.memo(({
  suggestion,
  index,
  componentId,
  isSelected,
  query,
  onClick,
  onHover,
}) => {
  return (
    <li
      id={`${componentId}-option-${index}`}
      className={`${dropdownStyles.suggestion} ${
        isSelected ? dropdownStyles.suggestionSelected : ''
      }`}
      role="option"
      aria-selected={isSelected}
      onClick={onClick}
      onMouseEnter={onHover}
    >
      <div className={dropdownStyles.suggestionText}>
        {highlightMatch(suggestion.text, query)}
      </div>
      <span className={dropdownStyles.suggestionType}>
        {suggestion.type}
      </span>
    </li>
  );
});

SuggestionItem.displayName = 'SuggestionItem';

export default SearchDropdown;